
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    TableContainer,
    InputBase,
    Menu,
    MenuItem,
    Pagination,
    FormControl,
    Select,
    InputLabel,
    CircularProgress,
} from '@mui/material';
import { Edit, Delete, MoreVert } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import apiUrl from '../../Config';

const DisplayAuthors = () => {
    const navigate = useNavigate();
    const [authors, setAuthors] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    const open = Boolean(anchorEl);

    const handleMenuClick = (event, author) => {
        setAnchorEl(event.currentTarget);
        setSelectedAuthor(author);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedAuthor(null);
    };

    const fetchAuthors = async () => {
        setLoading(true)
        try {
            const response = await axios.get(`${apiUrl}/authors/getauthors?page=${page}&limit=${limit}&searchTerm=${searchTerm}`);

            if (response.data.status) {
                setAuthors(response.data.authors || []);
                setTotalPages(response.data.totalPages || 1);
                setTotalItems(response.data.totalItems || response.data.authors?.length || 0);
            } else {
                setAuthors([]);
                setTotalPages(1);
                setTotalItems(0);
            }
        } catch (error) {
            console.log('Error fetching authors:', error);
            const message = error?.response?.data?.message || error.message || 'Failed to fetch testimonials'
            setError(message)
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        fetchAuthors();
    }, [page, limit]);

    const handleSearch = () => {
        setPage(1);
        fetchAuthors();
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${apiUrl}/authors/deleteauthor/${id}`);
            fetchAuthors(); // refresh list
            handleMenuClose();
        } catch (error) {
            console.log('Delete error:', error);
            const message = error?.response?.data?.message || error.message || 'Failed to fetch testimonials'
            setError(message)
        }
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    return (
        <Box p={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h3" my={2.5}>
                    Authors List
                </Typography>
                <Button variant="contained" onClick={() => navigate('/create-edit-authors')}>
                    Add Author
                </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',mb:2 }}>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'start', alignItems: 'center', gap: 2 }}>
                    <InputBase
                        placeholder="Search Author"
                        sx={{ border: '1px solid #ccc', px: 2, py: 0.5, borderRadius: 1, width: { xs: '70%', sm: 250 } }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="outlined" onClick={handleSearch}>
                        Search
                    </Button>
                </Box>
                <Box sx={{  display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Rows per page</InputLabel>
                        <Select value={limit} onChange={handleLimitChange} label="Rows per page">
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body2">Total Authors: {totalItems}</Typography>
                </Box>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Image</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {authors.map((author) => (
                                    <TableRow key={author.id}>
                                        <TableCell>{author.id}</TableCell>
                                        <TableCell>
                                            {author.imageUrl && (
                                                <img
                                                    src={author.imageUrl}
                                                    alt={author.name}
                                                    width={40}
                                                    height={40}
                                                    style={{ objectFit: 'cover', borderRadius: 4 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="h6" fontWeight={400}>
                                                {author.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{author.isActive ? 'Active' : 'Inactive'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuClick(e, author)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>


                    {/* Menu Actions */}
                    <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                        <MenuItem
                            onClick={() => {
                                navigate('/create-edit-authors', { state: { author: selectedAuthor } });
                                handleMenuClose();
                            }}
                        >
                            <Edit fontSize="small" sx={{ mr: 1 }} />
                            Edit
                        </MenuItem>
                        <MenuItem onClick={() => handleDelete(selectedAuthor.id)}>
                            <Delete fontSize="small" sx={{ mr: 1 }} />
                            Delete
                        </MenuItem>
                    </Menu>

                    <Box mt={2} display="flex" justifyContent="center" alignItems="center">
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            showFirstButton
                            showLastButton
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    height: 30,
                                    minWidth: 30,
                                    fontSize: '1rem',
                                    marginTop: '10px'
                                }
                            }}
                        />
                    </Box>
                </>
            )}

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

        </Box>

    );
};

export default DisplayAuthors;
